import { useCallback, useRef } from "react";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";

/** ★ 全局消息去重：通过 store 管理，resetConversation 时自动清空 */
function msgHash(role: string, text: string): string {
  return `${role}:${text}`;
}

// ── antd-mobile 全局样式（移动端基础重置 + 组件样式）──
// React 18 下官方完美支持 ✅
import "antd-mobile/es/global/global.css";

import AiFloatButton from "@/components/feature/AiFloatButton";
import VoiceRuntime from "./voice/VoiceRuntime";
import { useChatSSE } from "@/hooks/useChatSSE";
import { SSE_EVENTS_URL } from "@/constants";
import { useVoiceStore } from "./store/useVoiceStore";
import { getTime } from "./pages/ai-voice/engine";
import { resolveAudioUrl } from "@/api/aiChat";
import { isQueryMsgType, isBookingOrderMsg, buildQueryData, isRoutePath } from "./pages/ai-voice/message-utils";

/**
 * 服务端 SSE 消息处理：
 *   普通 AI 文字回复直接显示在聊天中
 *   navigation 类型消息 → 静默自动跳转到 d.data.route，不显示按钮/路径
 *
 * 后端 navigation 消息真实格式：
 *   {
 *     "msg_type": "navigation",
 *     "action": "navigate",
 *     "content": "好的，正在为您跳转到【酒店列表】页面～",
 *     "data": {"route": "/hotels", "label": "酒店列表"},
 *     "sender": {"uid":0,"name":"小伊",...}
 *   }
 */

function AppRuntime() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // SSE 消息处理
  const handleSSEMessage = useCallback(
    (msg: any) => {
      const d = msg?.data;
      if (!d) return;

      // ── connected 事件：通过 identity 字段识别 ──
      // ★ setSession 已在 useChatSSE.ts 的 onMessage 中统一处理（含 null 兜底），
      //   此处仅做日志记录，避免双重调用导致行为不一致
      if (d.identity) {
        const { uid, is_guest, session_id } = d.identity;
        console.log("[App] ★★★★ 收到 SSE connected 事件, identity:", JSON.stringify(d.identity));
        console.log("[App] 解构结果: uid=", uid, "is_guest=", is_guest, "session_id=", session_id, "类型:", typeof session_id);
        return;
      }

      // ── navigation 跳转指令（后端返回的页面跳转消息）──
      // 真实格式：{ msg_type:"navigation", action:"navigate", content:"好的，正在为您跳转到...", data:{route:"/hotels",label:"xxx"} }
      if (d.msg_type === "navigation" || d.action === "navigate") {
        const navData = d.data;
        let routePath: string | undefined;

        // ★ 兼容多种 route 字段名：route / path / url / to（后端可能有不同命名）
        const routeCandidates = [navData?.route, navData?.path, navData?.url, navData?.to];
        for (const cand of routeCandidates) {
          if (typeof cand === "string" && cand.length > 0) {
            routePath = cand;
            break;
          }
        }
        // 顶层字段兜底（data 为空时 route 可能在顶层）
        if (!routePath) {
          for (const key of ["route", "path", "url", "to"]) {
            const v = (d as any)[key];
            if (typeof v === "string" && v.length > 0) {
              routePath = v;
              break;
            }
          }
        }
        if (!routePath && typeof d.content === "string" && isRoutePath(d.content)) {
          // 兜底：content 本身就是路径
          routePath = d.content;
        }
        // 规范化：确保以 / 开头（兼容无前导斜杠的路径，如 "hotels" → "/hotels"）
        if (routePath && !routePath.startsWith("/")) {
          routePath = "/" + routePath;
        }
        console.log("[App] navigation 消息原始数据:", JSON.stringify(d), "提取到 route:", routePath);

        if (routePath) {
          console.log(`[SSE] navigation 自动跳转到: ${routePath}`, d);
          navigateRef.current(routePath);

          // 显示 AI 回复文字（如"好的，正在为您跳转到【酒店列表】页面～"），但不显示按钮/标签
          const voiceStore = useVoiceStore.getState();
          const contentText = (d.content || "").trim();
          if (contentText && !isRoutePath(contentText)) {
            // ★ 全局去重：防止 SSE 重连导致重复显示
            const navHash = msgHash("ai", contentText);
            if (voiceStore.hasSeenMessageHash(navHash)) {
              console.log(`[SSE] [去重] navigation 消息重复: "${contentText}"`);
              return;
            }
            voiceStore.addSeenMessageHash(navHash);

            if (voiceStore.pendingTypingMsgId !== null) {
              voiceStore.removeConversationMessage(voiceStore.pendingTypingMsgId);
              voiceStore.setPendingTypingMsgId(null);
            }
            voiceStore.appendConversationMessages([
              { role: "ai", text: contentText, time: getTime() } as any,
            ]);
          }
          return;
        }

        console.warn("[SSE] navigation 消息但未找到有效路径:", d);
        return;
      }

      // ── require_login 登录验证指令（预定前需先登录）──
      // 真实格式：{ msg_type:"require_login", action:"require_login", content:"亲爱的，预约前需要先登录哦~...", data:{reason:"booking_requires_auth"} }
      if (d.msg_type === "require_login" || d.action === "require_login") {
        console.log("[SSE] require_login → 跳转登录页", d);

        // 显示提示消息（如果有）
        const voiceStore = useVoiceStore.getState();
        const contentText = (d.content || "").trim();
        if (contentText) {
          const loginHash = msgHash("ai_require_login", contentText);
          if (!voiceStore.hasSeenMessageHash(loginHash)) {
            voiceStore.addSeenMessageHash(loginHash);

            if (voiceStore.pendingTypingMsgId !== null) {
              voiceStore.removeConversationMessage(voiceStore.pendingTypingMsgId);
              voiceStore.setPendingTypingMsgId(null);
            }
            voiceStore.appendConversationMessages([
              { role: "ai", text: contentText, time: getTime() } as any,
            ]);
          }
        }

        // ★ 跳转到登录页面（携带当前路径作为 redirect）
        const currentPath = window.location.pathname + window.location.search;
        navigateRef.current(`/wx-auth?redirect=${encodeURIComponent(currentPath)}`);
        return;
      }

      // ── TTS 文字转语音消息（msg_type=tts_audio / action=tts）──
      // 后端在 AI 回复后异步推送语音版本，需找到对应 AI 文字消息添加 audio_url 供播放
      if (d.msg_type === "tts_audio" || d.action === "tts") {
        // audio_url 可能在 data 内部，也可能在顶层
        // ★ 使用 ?? 而非 ||：避免空字符串触发 fallback
        const ttsAudioUrl = resolveAudioUrl(d.data?.audio_url ?? d.audio_url);
        const ttsContent = (d.content || "").trim();
        console.log(`[SSE] ★★★ 收到 TTS 消息, audio_url: ${ttsAudioUrl ? ttsAudioUrl.substring(0, 60) + "..." : "NULL"}, content:"${ttsContent.substring(0, 50)}", 完整data:`, JSON.stringify(d).substring(0, 200));

        if (ttsAudioUrl) {
          // ★ 闭包内保存 TTS 数据，供重试使用（避免异步回调读取到已被覆盖的外部变量）
          const _ttsAudioUrl = ttsAudioUrl;
          const _ttsContent = ttsContent;

          /**
           * 在消息列表中查找并绑定 TTS URL
           * @returns true 匹配成功，false 未找到可绑定消息
           */
          const tryBindTts = (): boolean => {
            const voiceStore = useVoiceStore.getState();
            const msgs = voiceStore.conversationMessages;

            // 策略0: 精确匹配到已绑定 URL 的消息 → 直接跳过（已处理过，SSE 重连导致的重复推送）
            if (_ttsContent) {
              for (let i = msgs.length - 1; i >= 0; i--) {
                const m = msgs[i];
                if (m.role === "ai" && m.url && m.text === _ttsContent) {
                  console.log(`[SSE] TTS ⊘ 跳过 msg#${m.id} (精确匹配但已绑定URL, 重复推送)`);
                  return true;
                }
              }
            }

            // 策略1: 精确匹配内容文本
            if (_ttsContent) {
              for (let i = msgs.length - 1; i >= 0; i--) {
                const m = msgs[i];
                if (m.role === "ai" && !m.url && m.text === _ttsContent) {
                  voiceStore.updateConversationMessage(m.id, { url: _ttsAudioUrl });
                  console.log(`[SSE] TTS ★ 已匹配 msg#${m.id} (精确匹配), 即将触发 autoPlay`);
                  return true;
                }
              }
            }

            // 策略2: 模糊匹配 —— AI 文本包含 TTS 内容，或 TTS 内容包含 AI 文本
            if (_ttsContent) {
              for (let i = msgs.length - 1; i >= 0; i--) {
                const m = msgs[i];
                if (m.role === "ai" && !m.url && !m.isTyping && m.text) {
                  if (m.text.includes(_ttsContent) || _ttsContent.includes(m.text)) {
                    voiceStore.updateConversationMessage(m.id, { url: _ttsAudioUrl });
                    console.log(`[SSE] TTS ★ 已匹配 msg#${m.id} (模糊匹配), 即将触发 autoPlay`);
                    return true;
                  }
                }
              }
            }

            // 策略3: 兜底 — 找到最近一条未绑定 TTS 的 AI 消息
            //         条件收紧：必须 !isTyping 且 ttsContent 与该消息文本存在部分重叠
            for (let i = msgs.length - 1; i >= 0; i--) {
              const m = msgs[i];
              if (m.role === "ai" && !m.url && !m.isTyping && m.text) {
                // 有 ttsContent 时要求至少部分重叠，避免误绑无关消息
                if (_ttsContent &&
                    !m.text.includes(_ttsContent) &&
                    !_ttsContent.includes(m.text)) {
                  continue;
                }
                voiceStore.updateConversationMessage(m.id, { url: _ttsAudioUrl });
                console.log(`[SSE] TTS ★ 已匹配 msg#${m.id} (兜底匹配), 即将触发 autoPlay`);
                return true;
              }
            }

            return false;
          };

          // 立即尝试绑定
          const matched = tryBindTts();

          // ★ TTS 比 AI 文本先到？启动重试（最多重试 5 次，间隔 300ms）
          if (!matched) {
            console.warn("[SSE] TTS → 首轮匹配失败，启动重试...");
            let retryCount = 0;
            const maxRetries = 5;
            // ★ 1.5s 总超时保护：避免僵尸定时器
            const globalTimeout = setTimeout(() => {
              clearInterval(retryTimer);
              console.warn("[SSE] TTS ⚠️ 全局超时 (1.5s)，放弃重试");
            }, 1800);
            const retryTimer = setInterval(() => {
              retryCount++;
              console.log(`[SSE] TTS → 重试第 ${retryCount}/${maxRetries} 次...`);
              if (tryBindTts()) {
                clearInterval(retryTimer);
                clearTimeout(globalTimeout);
              } else if (retryCount >= maxRetries) {
                clearInterval(retryTimer);
                clearTimeout(globalTimeout);
                // 所有重试失败，创建独立消息
                console.warn("[SSE] TTS ⚠️ 重试耗尽，创建独立音频消息");
                const voiceStore = useVoiceStore.getState();
                const msgs = voiceStore.conversationMessages;
                console.warn("[SSE] TTS ⚠️ 所有AI消息:");
                msgs.filter((m: any) => m.role === "ai").forEach((m: any) => {
                  console.log(`  msg#${m.id} url=${!!m.url} isTyping=${m.isTyping} text="${(m.text||"").substring(0,30)}"`);
                });
                const ttsHash = msgHash("ai_tts", _ttsAudioUrl);
                if (!voiceStore.hasSeenMessageHash(ttsHash)) {
                  voiceStore.addSeenMessageHash(ttsHash);
                  voiceStore.appendConversationMessages([
                    { role: "ai", text: _ttsContent || "🔊 语音播报", time: getTime(), url: _ttsAudioUrl } as any,
                  ]);
                  console.log(`[SSE] TTS 创建独立音频消息`);
                }
              }
            }, 300);
          }
        } else {
          console.warn("[SSE] TTS ⚠️ audio_url 为空! 完整消息:", JSON.stringify(d));
        }
        return;
      }

      // ── AI 欢迎语（ai-welcome 事件）：无条件显示，不受 sender 判定影响 ──
      if (msg.event === "ai_welcome" || msg.event === "ai-welcome") {
        const welcomeText = (d.content || "").trim();
        if (welcomeText) {
          const voiceStore = useVoiceStore.getState();
          // ★ 全局去重：防止 SSE 重连导致欢迎语重复
          const welcomeHash = msgHash("ai", welcomeText);
          if (voiceStore.hasSeenMessageHash(welcomeHash)) {
            console.log(`[SSE] [去重] AI欢迎语重复: "${welcomeText}"`);
            return;
          }
          voiceStore.addSeenMessageHash(welcomeHash);

          voiceStore.appendConversationMessages([
            { role: "ai", text: welcomeText, time: getTime() } as any,
          ]);
          console.log(`[SSE] AI欢迎语: "${welcomeText}"`);
        }
        return;
      }

      // ── 判断消息发送者 ──
      // Bot/AI: sender.uid === 0 或无 sender 或 source=bot/ai
      // User:   sender.uid > 0（用户自己发的文字或后端推回的转写结果）
      const senderUid = d.sender?.uid;
      const isFromUser = typeof senderUid === "number" && senderUid > 0;
      const isFromBot =
        !isFromUser &&
        (!d.sender ||
          senderUid === 0 ||   // ★ uid=0 = AI助手(小伊)的消息
          d.source === "bot" ||
          d.source === "ai");

      // 文本类消息（含 ai_streaming、new_message、ai_welcome 等）
      const isTextMsg =
        d.type === "text" ||
        d.type === "audio" ||
        msg.event === "message" ||
        msg.event === "ai_welcome" ||
        msg.event === "ai-welcome";

      if (!isTextMsg) return;

      const voiceStore = useVoiceStore.getState();
      // ★ 优先 display_content（后端指定的展示文本），回退 content / message
      const text = (d.display_content || d.content || d.message || "").trim();

      // ── 用户消息：后端 STT 转写结果（用于更新 🎤 语音占位为实际文字）──
      if (isFromUser && text) {
        console.log(`[SSE] 收到用户转写文字: "${text}"`);

        // ★ 去重检查：遍历所有消息，如果已存在同样文字的用户消息（非🎤占位），说明后端重复推送，直接忽略
        const msgs = voiceStore.conversationMessages;
        const hasDuplicate = msgs.some(
          (m) => m.role === "user" && m.text === text && !m.isVoice,
        );
        if (hasDuplicate) {
          console.log(`[SSE] 转写文字与已有用户消息重复，忽略: "${text}"`);
          return;
        }

        // 找最近的 🎤 语音占位消息，替换为实际转写文字（保留 isVoice=true 以显示语音气泡+文字）
        for (let i = msgs.length - 1; i >= 0; i--) {
          const m = msgs[i];
          if (m.role === "user" && m.isVoice && m.text === "\u{1F3A4}") {
            voiceStore.updateConversationMessage(m.id, {
              text,
            });
            console.log(`[SSE] 已更新语音占位 msg#${m.id} → "${text}"`);
            return;
          }
        }

        // 没找到占位，作为普通用户消息追加
        // ★ 全局去重：防止 SSE 重连导致重复显示
        const userHash = msgHash("user", text);
        if (voiceStore.hasSeenMessageHash(userHash)) {
          console.log(`[SSE] [去重] 用户消息重复: "${text}"`);
          return;
        }
        voiceStore.addSeenMessageHash(userHash);

        voiceStore.appendConversationMessages([
          { role: "user", text, time: getTime() } as any,
        ]);
        return;
      }

      if (!isFromBot) return;

        // ── 分隔符 "/"：不显示，清除占位消息 ──
        if (text === "/") {
          if (voiceStore.pendingTypingMsgId !== null) {
            voiceStore.removeConversationMessage(voiceStore.pendingTypingMsgId);
            voiceStore.setPendingTypingMsgId(null);
          }
          return;
        }

        // ── 路由路径文本（兜底：如果 content 就是 /xxx 格式）──
        if (isRoutePath(text)) {
          console.log(`[SSE] 检测到路由路径文本，自动跳转到: ${text}`);
          navigateRef.current(text);
          return;
        }

        // ── 普通 AI 回复文字（含查询类型消息：火车票/租车/美食/导游/机票/酒店/线路/接送等）──
        // ★ 查询类型判定与 queryData 规范化统一收敛到 message-utils（与历史加载逻辑共用）
        const isBookingOrder = isBookingOrderMsg(d);
        const isQueryMsg = isQueryMsgType(d.msg_type) || isBookingOrder;
        const queryData = buildQueryData(d);

        // ★ 全局去重：防止 SSE 重连导致重复显示
        // 查询消息用 msg_type+content 一起做 hash，避免同类数据重复
        const dedupKey = isQueryMsg ? `${d.msg_type || "order"}:${text}` : text;
        const aiHash = msgHash("ai", dedupKey);
        if (voiceStore.hasSeenMessageHash(aiHash)) {
          console.log(`[SSE] [去重] ${isQueryMsg ? `AI查询(${d.msg_type})` : "AI"}回复重复: "${text}"`);
          return;
        }
        voiceStore.addSeenMessageHash(aiHash);

        if (voiceStore.pendingTypingMsgId !== null) {
          voiceStore.removeConversationMessage(voiceStore.pendingTypingMsgId);
          voiceStore.setPendingTypingMsgId(null);
        }

        voiceStore.appendConversationMessages([
          {
            role: "ai",
            text,
            time: getTime(),
            _stream: true,
            url: resolveAudioUrl(d.audio_url ?? d.data?.audio_url),
            ...(queryData ? {
              msg_type: d.msg_type || (isBookingOrder ? "order" : undefined),
              queryData,
            } : {}),
          } as any,
        ]);

        // ★ 日志：是否附带了 audio_url
        if (d.audio_url) {
          const preview = typeof d.audio_url === "string"
            ? d.audio_url
            : Array.isArray(d.audio_url)
              ? `[base64数组, ${(d.audio_url[1] || "").substring(0, 40)}...]`
              : JSON.stringify(d.audio_url).substring(0, 60);
          console.log(`[SSE] AI文本消息附带 audio_url: ${preview}... (注: 这会导致 autoPlay=false!)`);
        } else {
          console.log(`[SSE] AI文本消息无 audio_url (等待 TTS 异步推送)`);
        }

        if (isQueryMsg) {
          console.log(`[SSE] AI查询消息(${d.msg_type}):`, d.data);
        }
    },
    [],
  );

  // 初始化聊天 SSE 长连接（始终连接，游客模式也可使用）
  useChatSSE({
    url: SSE_EVENTS_URL,
    enabled: true,
    onMessage: handleSSEMessage,
    debug: import.meta.env.DEV,
  });

  return (
    <>
      <AppRoutes />
      <VoiceRuntime />
      <AiFloatButton />
    </>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <AppRuntime />
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
